import {
  resolveEntityPhotoUrl,
  entityPhotoHasImage,
  activeDomainImage,
} from '../../utils/imageProxy';
import { ensureSignedProxyUrls } from '../../utils/signedImageProxyCache';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import userDefaultImage from '../../images/machineImageEmpty.svg';
import { useHasPermission } from '../../hooks/useHasPermission';
import { EntityImageEditor } from './EntityImageEditor';
import EntityPhotoFrame from './EntityPhotoFrame';
import { deleteEntityImage } from './entityImageApi';
import { syncListEntityImage } from './entityImageListSync';
import { getEntityImageConfig } from './entityImageConfig';
import { useSelector, useDispatch } from 'react-redux';
import OpenImageDialog from '../openImageDialog';
import { DeleteRecord } from '../deleteRecord';
import { useState, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Iconify from '../Iconify';
import '../../css/sites/siteImageActions.css';

const EntityImageActions = ({ entityType, fallbackImage = userDefaultImage }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const config = useMemo(() => getEntityImageConfig(entityType), [entityType]);
  const entityData = useSelector(config.detailsSelector);
  const canEdit = useHasPermission(config.permission);

  const [openImage, setOpenImage] = useState(false);
  const [deleteImage, setDeleteImage] = useState(false);
  const [modifyPhoto, setModifyPhoto] = useState(false);

  const photoOpts = useMemo(
    () => ({
      imageField: config.imageField,
      documentField: config.documentField,
    }),
    [config.imageField, config.documentField],
  );

  const displayUrl = resolveEntityPhotoUrl(entityData, {
    ...photoOpts,
    size: 'medium',
    fallback: fallbackImage,
  });
  const fullUrl = resolveEntityPhotoUrl(entityData, { ...photoOpts, size: 'default', fallback: '' });
  const hasImage = entityPhotoHasImage(entityData, { ...photoOpts, size: 'medium' });

  const openImageEditor = useCallback(() => {
    const activeImage = activeDomainImage(entityData, config.imageField);
    if (activeImage?.key_source) {
      ensureSignedProxyUrls([activeImage.key_source], { silent: true });
    }
    setModifyPhoto(true);
  }, [config.imageField, entityData]);

  const handleDeleteImage = useCallback(async () => {
    try {
      if (!entityData?.id) {
        throw new Error('Aucun identifiant disponible.');
      }
      const response = await deleteEntityImage(config, entityData.id);
      if (response.status === 200 || response.status === 201) {
        setDeleteImage(false);
        const nextDetails = {
          ...entityData,
          [config.imageField]: [],
        };
        if (config.documentField) {
          nextDetails[config.documentField] = [];
        }
        dispatch(config.setDetailsData(nextDetails));
        syncListEntityImage({
          entityId: entityData.id,
          imageField: config.imageField,
          image: null,
          documentField: config.documentField,
        });
      }
    } catch (error) {
      console.error(t("Erreur lors de la suppression de l'image :"), error);
      CustomErrorLogger(error, `EntityImageActions.delete.${entityType}`);
    }
  }, [config, dispatch, entityData, entityType, t]);

  const activeImage = activeDomainImage(entityData, config.imageField);

  return (
    <>
    <EntityPhotoFrame
      image={activeImage}
      src={displayUrl}
      alt={config.alt}
      className="siteImageActions_image_icon_box"
      showBlur={hasImage}
    >
      {hasImage ? (
        <Box>
          {canEdit && (
            <Box className="siteImageActions_edit_delete_icon_box">
              <Iconify
                className="siteImageActions_edit_icon"
                icon="ic:outline-edit"
                onClick={openImageEditor}
              />
              <Iconify
                className="siteImageActions_delete_icon"
                icon="ic:outline-delete"
                onClick={() => setDeleteImage(true)}
              />
            </Box>
          )}
          <Iconify
            className="siteImageActions_maximize_icon"
            icon="tabler:arrows-maximize"
            onClick={() => setOpenImage(true)}
          />
        </Box>
      ) : (
        canEdit && (
          <Iconify className="siteImageActions_plus_icon" icon="ic:baseline-plus" onClick={openImageEditor} />
        )
      )}
    </EntityPhotoFrame>

      {modifyPhoto && (
        <EntityImageEditor
          entityType={entityType}
          entityId={entityData?.id}
          entityData={entityData}
          modifyPhoto={modifyPhoto}
          setModifyPhoto={setModifyPhoto}
        />
      )}

      {openImage && (
        <OpenImageDialog openImage={openImage} setOpenImage={setOpenImage} profileUrl={fullUrl} />
      )}

      {deleteImage && (
        <DeleteRecord
          openDelete={deleteImage}
          setOpenDelete={setDeleteImage}
          handleDelete={handleDeleteImage}
          title={t(config.deleteTitle)}
        />
      )}
    </>
  );
};

export default EntityImageActions;
